import React from 'react';
import { Box, Flex, Text, Link, Divider } from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import StatusBadge from './StatusBadge';
import RefundDeadlineBadge from './RefundDeadlineBadge';
import {
  formatCaseId,
  formatCOPDisplay,
  formatRefundDateTime,
  formatPaymentDisplayId,
  formatPaymentMethodLabel,
  formatStudentDocument,
  REASON_LABELS,
  getStudentDisplayName,
  getPaymentInvoicingInfo,
  getRefundDeadlineInfo,
  REFUND_BUSINESS_DAYS_LIMIT,
} from 'api/administrador/refunds';

const LABEL_WIDTH = { base: '38%', md: '34%' };

const monoStyle = {
  fontFamily: 'mono',
  fontSize: 'sm',
  color: 'gray.600',
};

function SectionTitle({ children, isFirst = false }) {
  return (
    <Text
      fontSize="xs"
      fontWeight="bold"
      color="#574feb"
      letterSpacing="wider"
      textTransform="uppercase"
      pt={isFirst ? 0 : 5}
      pb={2}
    >
      {children}
    </Text>
  );
}

function SummaryRow({ label, children, showDivider = true }) {
  return (
    <>
      <Flex py={3} align="flex-start">
        <Box w={LABEL_WIDTH} flexShrink={0} pr={4}>
          <Text fontSize="sm" color="gray.700">
            {label}
          </Text>
        </Box>
        <Box flex="1" minW={0} textAlign="left">
          {children}
        </Box>
      </Flex>
      {showDivider && <Divider borderColor="gray.200" />}
    </>
  );
}

export default function CaseSummaryCard({ refund }) {
  const letterUrl = refund.signed_letter?.url;
  const payment = refund.payment;
  const paymentDate = payment?.payment_date || payment?.createdAt;
  const hasDiscount = Number(refund.gateway_discount_percent) > 0;
  const invoicingInfo = getPaymentInvoicingInfo(payment);
  const deadlineInfo = getRefundDeadlineInfo(refund);

  return (
    <Box bg="white" borderRadius="lg" border="1px solid #9e46d8" overflow="hidden">
      <Box bg="#574feb" px={6} py={3}>
        <Flex align="center" gap={3}>
          <Text color="white" fontWeight="bold" fontSize="lg">
            Devolución {formatCaseId(refund.id)}
          </Text>
          <StatusBadge status={refund.status} />
        </Flex>
      </Box>

      <Box px={6} pt={4} pb={5}>
        <SectionTitle isFirst>Estudiante</SectionTitle>

        <SummaryRow label="Nombre">
          <Link
            as={RouterLink}
            to={`/plataforma/administrador/ver-estudiante/${refund.student?.id}`}
            color="#574feb"
            fontSize="sm"
            fontWeight="medium"
            _hover={{ textDecoration: 'underline' }}
          >
            {getStudentDisplayName(refund.student)}
          </Link>
        </SummaryRow>

        <SummaryRow label="Código">
          <Text fontSize="sm" color="gray.600">
            {refund.student?.id || '—'}
          </Text>
        </SummaryRow>

        <SummaryRow label="Documento">
          <Text fontSize="sm" color="gray.600">
            {formatStudentDocument(refund.student)}
          </Text>
        </SummaryRow>

        <SummaryRow label="Programa" showDivider={false}>
          <Text fontSize="sm" color="gray.800">
            {refund.program_type?.name || '—'}
          </Text>
        </SummaryRow>

        <SectionTitle>Pago vinculado</SectionTitle>

        <SummaryRow label="ID Pago">
          <Text {...monoStyle}>{formatPaymentDisplayId(payment)}</Text>
        </SummaryRow>

        <SummaryRow label="Fecha de pago">
          <Text {...monoStyle}>{formatRefundDateTime(paymentDate)}</Text>
        </SummaryRow>

        <SummaryRow label="Método">
          <Text fontSize="sm" color="gray.800">
            {formatPaymentMethodLabel(payment?.method)}
          </Text>
        </SummaryRow>

        <SummaryRow label="Facturación">
          <Box>
            <Text fontSize="sm" fontWeight="bold" color="gray.800">
              {invoicingInfo.billingStatusLabel}
            </Text>
            <Text
              fontSize="sm"
              color={invoicingInfo.requiresCreditNote ? '#B45309' : 'gray.600'}
              mt={0.5}
            >
              {invoicingInfo.creditNoteLabel}
            </Text>
          </Box>
        </SummaryRow>

        <SummaryRow label="Valor pagado">
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {formatCOPDisplay(refund.paid_amount)}
          </Text>
        </SummaryRow>

        {hasDiscount && (
          <SummaryRow label={`Descuento (${refund.gateway_discount_percent}%)`}>
            <Text fontSize="sm" fontWeight="bold" color="red.500">
              -{formatCOPDisplay(refund.gateway_discount_amount)}
            </Text>
          </SummaryRow>
        )}

        <SummaryRow label="Valor a devolver" showDivider={deadlineInfo.applies}>
          <Text fontSize="sm" fontWeight="bold" color="#574feb">
            {formatCOPDisplay(refund.refund_amount)}
          </Text>
        </SummaryRow>

        {deadlineInfo.applies && (
          <>
            <SectionTitle>Plazo de devolución</SectionTitle>

            <SummaryRow label="Registrado">
              <Text fontSize="sm" color="gray.600">
                {deadlineInfo.registeredAtFormatted}
              </Text>
            </SummaryRow>

            <SummaryRow label="Fecha límite">
              <Text fontSize="sm" fontWeight="bold" color="gray.800">
                {deadlineInfo.dueDateFormatted}
              </Text>
            </SummaryRow>

            <SummaryRow label="Compromiso" showDivider={false}>
              <Box>
                <Text fontSize="sm" color="gray.600" mb={2}>
                  {REFUND_BUSINESS_DAYS_LIMIT} días hábiles (lun–vie) desde el registro
                </Text>
                <RefundDeadlineBadge refund={refund} />
              </Box>
            </SummaryRow>
          </>
        )}

        <SectionTitle>Motivo de la devolución</SectionTitle>

        <SummaryRow label="Motivo" showDivider={!!refund.reason_detail || !!letterUrl}>
          <Text fontSize="sm" fontWeight="bold" color="gray.800">
            {REASON_LABELS[refund.reason] || refund.reason}
          </Text>
        </SummaryRow>

        {refund.reason_detail && (
          <Box py={3} borderBottom={letterUrl ? '1px solid' : undefined} borderColor="gray.200">
            <Flex align="flex-start">
              <Box w={LABEL_WIDTH} flexShrink={0} pr={4}>
                <Text fontSize="sm" color="gray.700">
                  Detalle
                </Text>
              </Box>
              <Box flex="1" minW={0}>
                <Text fontSize="sm" color="gray.800" lineHeight="tall">
                  {refund.reason_detail}
                </Text>
              </Box>
            </Flex>
          </Box>
        )}

        {letterUrl && (
          <SummaryRow label="Carta firmada" showDivider={false}>
            <Link href={letterUrl} isExternal color="#574feb" fontSize="sm" fontWeight="medium">
              Ver PDF
            </Link>
          </SummaryRow>
        )}
      </Box>
    </Box>
  );
}
